import React, { useEffect, useMemo, useState } from 'react';
import { AxiosResponse } from 'axios';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import { checkPermission } from '../../../../core/permissons/CheckPermissons';
import { Link, useNavigate } from "react-router-dom";
import { showError } from '../../../../core/alert/sweetAlertConfig';
import CollapseHeader from '../../../../core/common/collapse-header'
import { all_routes } from '../../../router/all_routes';
import GlassSelect from '../../../../core/common/glassSelect/GlassSelect';

/** DTOs — keep in sync with your project-wide types */
export interface LocationDTO {
  id: number;
  name: string;
  address: string;
  lat: number;
  lng: number;
  description?: string;
  createdBy: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt?: string;
}
export interface BuildingDTO {
  id: number;
  name: string;
  noOfFloors: number;
  locationName: string;
  locationId: number;
  description?: string | null;
}
export interface FloorDTO {
  id: number;
  name: string;
  floorNumber: number;
  buildingId: number;
  buildingName: string;
  locationName: string;
  createdBy: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt?: string;
}
export interface ParkingSlotDTO {
  id: number;
  slotName: string;
  x: string;
  y: string;
  locationId?: number | null;
  floorId?: number | null;
  locationName?: string | null;
  floorName?: string | null;
  status: string;
}

/** Helpers */
const numericFirstSort = (a: string, b: string): number => {
  const ax = parseInt(a.replace(/\D/g, ''), 10);
  const bx = parseInt(b.replace(/\D/g, ''), 10);
  const aNum = Number.isFinite(ax);
  const bNum = Number.isFinite(bx);
  if (aNum && bNum) return ax - bx;
  if (aNum && !bNum) return -1;
  if (!aNum && bNum) return 1;
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
};

type Props = {
  /** If parent passes locations/floors we’ll use them; otherwise we fetch. */
  locations?: LocationDTO[];
  floors?: FloorDTO[];
  onOpenSlot?: (slotId: number) => void;
};

const ViewParkingSlot: React.FC<Props> = ({ locations: locationsProp = [], floors: floorsProp = [], onOpenSlot }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  /** Filter states */
  const [locations, setLocations] = useState<LocationDTO[]>(locationsProp);
  const [buildings, setBuildings] = useState<BuildingDTO[]>([]);
  const [floors, setFloors] = useState<FloorDTO[]>(floorsProp);

  const [selectedLocationId, setSelectedLocationId] = useState<number | ''>('');
  const [selectedBuildingId, setSelectedBuildingId] = useState<number | ''>('');
  const [selectedFloorId, setSelectedFloorId] = useState<number | ''>('');
  const [statusFilter, setStatusFilter] = useState<string>(''); // '', 'available', 'occupied', etc.
  const [search, setSearch] = useState<string>('');

  const [parkingSlotsRaw, setParkingSlotsRaw] = useState<ParkingSlotDTO[]>([]);
  const [hoveredId, setHoveredId] = useState<number | null>(null);

  /** Fetch locations if not provided by parent */
  const fetchLocations = async () => {
    if (locationsProp.length > 0) return; // already have
    try {
      const payload = { filterType: '', filterValue: '', date: '' };
      const res: AxiosResponse = await apiDefinitions.getLocations('1', payload);
      if (res.status === 200) {
        const data = (res.data?.data?.locations || []) as LocationDTO[];
        setLocations(data);
      } else {
        showError(res.data?.statusDescription || 'Failed to fetch locations');
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        showError('Unauthorized: Invalid or expired token. Please log in again.');
        localStorage.removeItem('token');
      } else {
        showError('Failed to fetch locations. Please try again.');
      }
    }
  };

  /** Fetch buildings when location changes */
  const fetchBuildings = async (locationId: number) => {
    try {
      const res: AxiosResponse = await apiDefinitions.getBuildingsByLocationId(locationId);
      if (res.status === 200) {
        const data = (res.data?.data || []) as BuildingDTO[];
        setBuildings(data);
      } else {
        showError(res.data?.statusDescription || 'Failed to fetch buildings');
        setBuildings([]);
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        showError('Unauthorized: Invalid or expired token. Please log in again.');
        localStorage.removeItem('token');
      } else {
        showError('Failed to fetch buildings. Please try again.');
      }
      setBuildings([]);
    }
  };
  const navItems = [
  { label: 'View Rooms', icon: 'ti ti-settings', link: all_routes.viewrooms, permission: 'ROLE_ADD_LOCATION' },
  { label: 'Room Register', icon: 'ti ti-settings', link: all_routes.roomregister, permission: 'ROLE_ADD_LOCATION' },
  { label: 'Add Location', icon: 'ti ti-plus', link: all_routes.location, permission: 'ROLE_ADD_LOCATION' },
    { label: 'View Parking Slots', icon: 'ti ti-settings', link: all_routes.viewparkingslots, permission: 'ROLE_GET_PARKING_SLOT' },
  { label: 'Parking Slot Register', icon: 'ti ti-settings', link: all_routes.parkingslotregister, permission: 'ROLE_GET_PARKING_SLOT' },
];


  /** Fetch floors when building changes */
  const fetchFloors = async (buildingId: number) => {
    try {
      const res: AxiosResponse = await apiDefinitions.getFloorsByBuildingId(buildingId);
      if (res.status === 200) {
        const data = (res.data?.data || []) as FloorDTO[];
        setFloors(data);
      } else {
        showError(res.data?.statusDescription || 'Failed to fetch floors');
        setFloors([]);
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        showError('Unauthorized: Invalid or expired token. Please log in again.');
        localStorage.removeItem('token');
      } else {
        showError('Failed to fetch floors. Please try again.');
      }
      setFloors([]);
    }
  };

  /** Compose API payload by specificity: floorId > buildingId > locationId */
  const buildAPIPayload = () => {
    if (selectedFloorId) {
      return { filterType: 'floorId', filterValue: String(selectedFloorId), date: '' };
    }
    if (selectedBuildingId) {
      return { filterType: 'buildingId', filterValue: String(selectedBuildingId), date: '' };
    }
    if (selectedLocationId) {
      return { filterType: 'locationId', filterValue: String(selectedLocationId), date: '' };
    }
    return { filterType: '', filterValue: '', date: '' };
  };

  /** Fetch parking slots */
  const fetchParkingSlots = async () => {
    if (!checkPermission('ROLE_GET_PARKING_SLOT')) {
      setError('You do not have permission to view parking slots.');
      return;
    }
    setIsLoading(true);
    setError(null);

    try {
      const payload = buildAPIPayload();
      const res: AxiosResponse = await apiDefinitions.getAllParkingSlots('1', payload);

      if (res.status === 200) {
        const data = (res.data?.data || []) as ParkingSlotDTO[];

        const withNames = data.map((slot: ParkingSlotDTO) => ({
          ...slot,
          locationName: slot.locationId
            ? locations.find((l) => l.id === slot.locationId)?.name || null
            : slot.locationName ?? null,
          floorName: slot.floorId
            ? floors.find((f) => f.id === slot.floorId)?.name || null
            : slot.floorName ?? null,
        }));

        const sorted = withNames
          .slice()
          .sort((a: ParkingSlotDTO, b: ParkingSlotDTO) => numericFirstSort(a.slotName, b.slotName));

        setParkingSlotsRaw(sorted);
        if (sorted.length === 0) setError('No parking slots found.');
      } else {
        showError(res.data?.statusDescription || 'Failed to fetch parking slots');
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        showError('Unauthorized: Invalid or expired token. Please log in again.');
        localStorage.removeItem('token');
        setError('Unauthorized');
      } else if (err.response?.status === 404) {
        setError('No parking slots found.');
      } else {
        showError('Failed to fetch parking slots. Please try again.');
        setError('Failed to fetch parking slots.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  /** Init: ensure locations present, then initial fetch */
  useEffect(() => {
    fetchLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** When location changes → reset & fetch buildings */
  useEffect(() => {
    if (selectedLocationId) {
      setSelectedBuildingId('');
      setSelectedFloorId('');
      fetchBuildings(Number(selectedLocationId));
    } else {
      setBuildings([]);
      setFloors(floorsProp.length ? floorsProp : []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocationId]);

  /** When building changes → reset & fetch floors */
  useEffect(() => {
    if (selectedBuildingId) {
      setSelectedFloorId('');
      fetchFloors(Number(selectedBuildingId));
    } else if (selectedLocationId) {
      // when clearing building, but still have a location
      setFloors(floorsProp.length ? floorsProp : []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBuildingId]);

  /** Refetch slots when hierarchy filters change */
  useEffect(() => {
    fetchParkingSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocationId, selectedBuildingId, selectedFloorId]);

  /** Client-side filter for status + search */
  const parkingSlots = useMemo(() => {
    let list = parkingSlotsRaw;

    if (statusFilter.trim()) {
      const sf = statusFilter.trim().toLowerCase();
      list = list.filter((s) => String(s.status).toLowerCase() === sf);
    }

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (s) =>
          s.slotName.toLowerCase().includes(q) ||
          (s.locationName || '').toLowerCase().includes(q) ||
          (s.floorName || '').toLowerCase().includes(q)
      );
    }

    return list;
  }, [parkingSlotsRaw, statusFilter, search]);

  const openParkingSlot = (id: number) => {
    if (onOpenSlot) onOpenSlot(id);
  };

  const clearFilters = () => {
    setSelectedLocationId('');
    setSelectedBuildingId('');
    setSelectedFloorId('');
    setStatusFilter('');
    setSearch('');
  };

  return (
     <div className="page-wrapper">
      <div className="content">
        {/* Header + tabs */}
        <div className="row">
          <div className="col-md-12">
            <div className="page-header">
              <div className="row align-items-center">
                <div className="col-8">
                  <h4 className="page-title">Administration/Location/View Parking Slots</h4>
                </div>
                <div className="col-4 text-end">
                  <div className="head-icons"><CollapseHeader /></div>
                </div>
              </div>
            </div>

                        <div className="card">
                          <div className="card-body pb-0 pt-2">
                            <ul className="nav nav-tabs nav-tabs-bottom" style={{ gap: '12px', display: 'flex' }}>
                              {navItems.map((item, index) =>
                                item.permission && checkPermission(item.permission) ? (
                                  <li className="nav-item me-item" key={index}>
                                    <Link to={item.link} className={`nav-link px-0 ${index === 3 ? 'active' : ''}`}>
                                      <i className={item.icon}></i> {item.label}
                                    </Link>
                                  </li>
                                ) : null
                              )}
                            </ul>
                          </div>
                        </div>
            {/* Filters Card */}
            <div className="card">
              <div className="card-header justify-content-between">
                <div className="card-title">Filters</div>
              </div>
              <div className="card-body">
                <div className="row g-2 align-items-end">
                  {/* Location */}
                  <div className="col-12 col-sm-6 col-md-3">
                    <label className="form-label">Location</label>
                    <GlassSelect
                      options={[{ value: '', label: 'All Locations' }, ...locations.map((loc) => ({ value: loc.id, label: loc.name }))]}
                      value={selectedLocationId}
                      onChange={(val) => setSelectedLocationId(val ? Number(val) : '')}
                      placeholder="All Locations"
                      disabled={isLoading}
                      searchable
                      allowClear
                    />
                  </div>

                  {/* Building */}
                  <div className="col-12 col-sm-6 col-md-3">
                    <label className="form-label">Building</label>
                    <GlassSelect
                      options={[{ value: '', label: 'All Buildings' }, ...buildings.map((b) => ({ value: b.id, label: b.name }))]}
                      value={selectedBuildingId}
                      onChange={(val) => setSelectedBuildingId(val ? Number(val) : '')}
                      placeholder="All Buildings"
                      disabled={isLoading || !selectedLocationId}
                      searchable
                      allowClear
                    />
                  </div>

                  {/* Floor */}
                  <div className="col-12 col-sm-6 col-md-3">
                    <label className="form-label">Floor</label>
                    <GlassSelect
                      options={[{ value: '', label: 'All Floors' }, ...floors.map((f) => ({ value: f.id, label: f.name }))]}
                      value={selectedFloorId}
                      onChange={(val) => setSelectedFloorId(val ? Number(val) : '')}
                      placeholder="All Floors"
                      disabled={isLoading || !selectedBuildingId}
                      searchable
                      allowClear
                    />
                  </div>

                  {/* Status */}
                  <div className="col-12 col-sm-6 col-md-2">
                    <label className="form-label">Status</label>
                    <GlassSelect
                      options={[
                        { value: '', label: 'All' },
                        { value: 'available', label: 'Available' },
                        { value: 'occupied', label: 'Occupied' },
                        { value: 'blocked', label: 'Blocked' },
                      ]}
                      value={statusFilter}
                      onChange={(val) => setStatusFilter(val || '')}
                      placeholder="All Statuses"
                      disabled={isLoading}
                      allowClear
                    />
                  </div>

                  {/* Search */}
                  <div className="col-12 col-sm-8 col-md-4">
                    <label className="form-label">Search</label>
                    <input
                      className="form-control"
                      type="text"
                      placeholder="Search by slot name / floor / location"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      disabled={isLoading}
                    />
                  </div>

                  {/* Buttons */}
                  <div className="col-12 col-sm-4 col-md-2 d-grid">
                    <button className="btn btn-secondary" onClick={clearFilters} disabled={isLoading}>
                      Clear Filters
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Slots Card */}
            <div className="card">
              <div className="card-header justify-content-between">
                <div className="card-title">Parking Slots</div>
              </div>
              <div className="card-body">
                {isLoading && (
                  <div className="text-center my-3">
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                )}

                {!isLoading && error && (
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                )}

                {!isLoading && !error && parkingSlots.length === 0 && (
                  <div className="alert alert-info" role="alert">
                    No parking slots to display.
                  </div>
                )}

                {!isLoading && parkingSlots.length > 0 && (
                  <div className="row g-2">
                    {parkingSlots.map((slot: ParkingSlotDTO) => (
                      <div className="col-12 col-sm-6 col-md-4 col-lg-3" key={slot.id}>
                        <div
                          className="card"
                          onClick={() => openParkingSlot(slot.id)}
                          title={slot.slotName}
                          onMouseEnter={() => setHoveredId(slot.id)}
                          onMouseLeave={() =>
                            setHoveredId((prev) => (prev === slot.id ? null : prev))
                          }
                          style={{
                            transition: 'transform 0.2s',
                            transform: hoveredId === slot.id ? 'scale(1.02)' : 'scale(1)',
                            cursor: 'pointer',
                          }}
                        >
                          <div className="card-body py-1 px-2 d-flex justify-content-between align-items-center">
                            <span className="fw-semibold text-dark parking-slot-card__label">
                              {slot.slotName}
                            </span>
                            <span className="badge bg-light text-dark">
                              {slot.floorName || slot.locationName || '-'}
                            </span>
                          </div>
                          <div className="card-footer py-1 d-flex justify-content-between">
                            <span
                              className={`badge ${
                                String(slot.status).toLowerCase() === 'available'
                                  ? 'bg-success'
                                  : String(slot.status).toLowerCase() === 'occupied'
                                  ? 'bg-danger'
                                  : 'bg-secondary'
                              }`}
                            >
                              {slot.status}
                            </span>
                            {/* <small className="text-muted">
                              {typeof slot.x === 'string' && typeof slot.y === 'string'
                                ? `(${slot.x}, ${slot.y})`
                                : ''}
                            </small> */}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>
      </div>
  );
};

export default ViewParkingSlot;
